import { useState } from 'react'
import { motion } from 'framer-motion'
import { FaCheck } from 'react-icons/fa'
import RealisticPlate from '@/components/shop/RealisticPlate'
import type { PlateSize, PlateType, VehicleCategory } from '@/services/rules/plateRulesEngine'

interface UpsellModalProps {
    plateNumber: string // e.g. "B A 3460"
    onAccept: () => void
    onDecline: () => void
}

interface UmweltplaketteModalProps {
    plateNumber: string
    onAccept: (emissionClass: 2 | 3 | 4) => void
    onDecline: () => void
}

type EmissionClass = 2 | 3 | 4
const EMISSION_INFO: Record<EmissionClass, { color: string; label: string; bg: string; fg: string }> = {
    2: { color: 'rot', label: 'Rot (Schadstoffgruppe 2)', bg: '#d32f2f', fg: '#fff' },
    3: { color: 'gelb', label: 'Gelb (Schadstoffgruppe 3)', bg: '#fbc02d', fg: '#111' },
    4: { color: 'grün', label: 'Grün (Schadstoffgruppe 4)', bg: '#2e8b34', fg: '#fff' },
}

interface CarbonUpgradeModalProps extends UpsellModalProps {
    cityCode?: string
    letters?: string
    numbers?: string
    plateSize?: PlateSize | null
    plateType?: PlateType | null
    vehicleCategory?: VehicleCategory | null
}

// ── Umweltplakette Modal ────────────────────────────────────────────────────

export function UmweltplaketteModal({ plateNumber, onAccept, onDecline }: UmweltplaketteModalProps) {
    const [selectedClass, setSelectedClass] = useState<EmissionClass>(4)
    const [confirmed, setConfirmed] = useState(false)
    const info = EMISSION_INFO[selectedClass]
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl"
            >
                {/* Title */}
                <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight">
                    <span className="text-gray-900">Offizielle Umwelt</span>
                    <span className="text-green-600">plakette</span>
                </h2>
                <p className="text-lg font-black text-gray-900 mt-1 uppercase tracking-wide">
                    Direkt mitbestellen{' '}
                    <span className="font-normal">für nur</span>{' '}
                    <span className="text-green-600 font-black">5,00 €</span>
                </p>

                {/* Content */}
                <div className="flex flex-col sm:flex-row gap-8 mt-6 items-center sm:items-start">
                    {/* Realistic Umweltplakette */}
                    <div className="flex-shrink-0 flex flex-col items-center">
                        <div className="relative" style={{ width: 220, height: 220 }}>
                            {/* Outermost thin black ring */}
                            <div
                                className="absolute inset-0 rounded-full"
                                style={{
                                    background: '#444',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.4)',
                                }}
                            />
                            {/* White ring */}
                            <div
                                className="absolute rounded-full bg-white"
                                style={{ top: 1, left: 1, right: 1, bottom: 1 }}
                            />
                            {/* Inner black ring */}
                            <div
                                className="absolute rounded-full"
                                style={{ top: 4, left: 4, right: 4, bottom: 4, background: '#333' }}
                            />
                            {/* Green fill */}
                            <div
                                className="absolute rounded-full overflow-hidden"
                                style={{
                                    top: 9,
                                    left: 9,
                                    right: 9,
                                    bottom: 9,
                                    background: info.bg,
                                }}
                            >
                                {/* Umwelt ZONE label - top left */}
                                <div
                                    className="absolute flex flex-col items-center"
                                    style={{ top: 50, left: 28, padding: '3px 7px 2px', lineHeight: 1, background: 'white', borderRadius: '2px', border: '1.5px solid #222', outline: '1.5px solid white', outlineOffset: '0px' }}
                                >
                                    <div
                                        className="rounded-full border-[2.5px] border-red-600 flex items-center justify-center"
                                        style={{ width: 22, height: 22 }}
                                    >
                                        <span className="font-black text-red-600" style={{ fontSize: 4.5, lineHeight: 1 }}>Umwelt</span>
                                    </div>
                                    <span className="font-black text-gray-900" style={{ fontSize: 8.5, letterSpacing: '0.08em', marginTop: '1px' }}>ZONE</span>
                                </div>

                                {/* Large number 4 */}
                                <span
                                    className="absolute select-none"
                                    style={{
                                        fontFamily: "'Sauerkrauto', 'Arial Black', sans-serif",
                                        fontWeight: 'normal',
                                        color: info.fg,
                                        fontSize: 140,
                                        lineHeight: 1,
                                        top: '25%',
                                        left: '50%',
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                >
                                    {selectedClass}
                                </span>

                                {/* Plate number box */}
                                <div
                                    className="absolute bg-white flex items-center justify-center"
                                    style={{
                                        bottom: 42,
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        padding: '4px 16px',
                                        border: '2px solid #222',
                                        minWidth: 100,
                                    }}
                                >
                                    <span
                                        className="whitespace-nowrap"
                                        style={{
                                            fontFamily: "'Sauerkrauto', 'Arial Black', sans-serif",
                                            fontWeight: 'normal',
                                            fontSize: 24,
                                            color: '#111',
                                            letterSpacing: '0.05em',
                                        }}
                                    >
                                        {plateNumber}
                                    </span>
                                </div>

                                {/* AU-Werkstatt seal */}
                                <div
                                    className="absolute"
                                    style={{ bottom: 4, left: '50%', transform: 'translateX(-50%)' }}
                                >
                                    <div
                                        className="rounded-full bg-white border-2 border-gray-400 flex items-center justify-center"
                                        style={{ width: 50, height: 50 }}
                                    >
                                        <svg viewBox="0 0 40 40" width="50" height="50">
                                            <defs>
                                                <path id="sealTop" d="M 6,20 a 14,14 0 1,1 28,0" />
                                                <path id="sealBot" d="M 6,20 a 14,14 0 1,0 28,0" />
                                            </defs>
                                            <text fontSize="3.6" fill="#333" fontWeight="600">
                                                <textPath href="#sealTop" startOffset="50%" textAnchor="middle">Anerkannte AU-Werkstatt</textPath>
                                            </text>
                                            <text fontSize="3.2" fill="#333">
                                                <textPath href="#sealBot" startOffset="50%" textAnchor="middle">★ ★ ★ ★</textPath>
                                            </text>
                                        </svg>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Features */}
                    <div className="flex-1">
                        <p className="text-gray-600 text-sm mb-4">
                            Voraussetzung für das Befahren der Umweltzonen in über 50 Städten.
                            Ohne gültige Plakette drohen hohe Bußgelder.
                        </p>

                        {/* Emission class selector */}
                        <label className="block text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">
                            Schadstoffgruppe Ihres Fahrzeugs
                        </label>
                        <div className="grid grid-cols-3 gap-2 mb-3">
                            {([2, 3, 4] as EmissionClass[]).map(cls => {
                                const c = EMISSION_INFO[cls]
                                const active = selectedClass === cls
                                return (
                                    <button
                                        key={cls}
                                        type="button"
                                        onClick={() => setSelectedClass(cls)}
                                        className={`py-2.5 px-2 rounded-lg border-2 text-xs font-bold transition-all ${active ? 'border-gray-800 shadow-md scale-[1.02]' : 'border-gray-200 hover:border-gray-400'}`}
                                        style={active ? { background: c.bg, color: c.fg } : {}}
                                    >
                                        <div className="text-lg">{cls}</div>
                                        <div className="uppercase tracking-wide">{c.color}</div>
                                    </button>
                                )
                            })}
                        </div>

                        <label className="flex items-start gap-2 mb-4 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={confirmed}
                                onChange={e => setConfirmed(e.target.checked)}
                                className="mt-1 w-4 h-4 accent-green-600 flex-shrink-0"
                            />
                            <span className="text-xs text-gray-600 leading-relaxed">
                                Ich bestätige, dass mein Fahrzeug in die gewählte Schadstoffgruppe fällt und werde auf Anfrage den Fahrzeugschein vorlegen.
                            </span>
                        </label>

                        <ul className="space-y-2">
                            <li className="flex items-center gap-3 text-xs text-gray-700">
                                <FaCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                Maschinelle Beschriftung, kein Ausbleichen
                            </li>
                            <li className="flex items-center gap-3 text-xs text-gray-700">
                                <FaCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
                                DIN zertifiziert &amp; amtlich anerkannt
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-6 mt-8">
                    <button
                        onClick={() => onAccept(selectedClass)}
                        disabled={!confirmed}
                        className={`px-8 py-3 font-bold rounded-lg shadow-lg transition-all uppercase tracking-wide ${confirmed ? 'bg-green-600 hover:bg-green-500 text-white hover:scale-[1.02] active:scale-[0.98]' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                    >
                        Ja, hinzufügen
                    </button>
                    <button
                        onClick={onDecline}
                        className="text-gray-400 hover:text-gray-600 font-medium text-sm uppercase tracking-wide transition-colors"
                    >
                        Nein, danke
                    </button>
                </div>
            </motion.div>
        </div>
    )
}

// ── Carbon Upgrade Modal ────────────────────────────────────────────────────

const DEFAULT_PLATE_SIZE: PlateSize = { width: 520, height: 110, layout: 'einzeilig' }

export function CarbonUpgradeModal({
    onAccept,
    onDecline,
    cityCode = '',
    letters = '',
    numbers = '',
    plateSize,
    plateType,
    vehicleCategory,
}: CarbonUpgradeModalProps) {
    const resolvedSize = plateSize ?? DEFAULT_PLATE_SIZE
    const resolvedType = plateType ?? 'REGULAR'
    const resolvedCategory = vehicleCategory ?? 'PKW'
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-2xl p-6 sm:p-8 max-w-2xl w-full shadow-2xl"
            >
                {/* Title */}
                <h2 className="text-2xl sm:text-3xl font-black text-blue-600 uppercase tracking-tight">
                    Carbon Upgrade
                </h2>
                <p className="text-lg font-black text-gray-900 mt-1 uppercase tracking-wide">
                    Direkt mitbestellen{' '}
                    <span className="font-normal">für je</span>{' '}
                    <span className="text-blue-600 font-black">4,90 €</span>
                </p>

                {/* Content */}
                <div className="flex flex-col sm:flex-row gap-6 mt-6">
                    {/* Left: Carbon circle + arrow + plate */}
                    <div className="flex-shrink-0 flex flex-col items-center" style={{ width: 220 }}>
                        {/* Carbon texture circle with blue border */}
                        <div className="relative" style={{ width: 150, height: 150 }}>
                            <div
                                className="w-full h-full rounded-full overflow-hidden"
                                style={{
                                    border: '4px solid #1565c0',
                                    boxShadow: '0 4px 16px rgba(21,101,192,0.3)',
                                }}
                            >
                                <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
                                    <defs>
                                        <pattern id="carbonPattern" width="8" height="8" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                                            <rect width="8" height="8" fill="#2a2a2a" />
                                            <rect width="4" height="4" fill="#3a3a3a" />
                                            <rect x="4" y="4" width="4" height="4" fill="#3a3a3a" />
                                        </pattern>
                                        <pattern id="carbonOverlay" width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
                                            <rect width="4" height="4" fill="transparent" />
                                            <rect width="2" height="2" fill="rgba(255,255,255,0.03)" />
                                            <rect x="2" y="2" width="2" height="2" fill="rgba(255,255,255,0.03)" />
                                        </pattern>
                                        <radialGradient id="carbonSheen" cx="35%" cy="35%">
                                            <stop offset="0%" stopColor="rgba(255,255,255,0.12)" />
                                            <stop offset="100%" stopColor="rgba(0,0,0,0.1)" />
                                        </radialGradient>
                                    </defs>
                                    <rect width="100%" height="100%" fill="url(#carbonPattern)" />
                                    <rect width="100%" height="100%" fill="url(#carbonOverlay)" />
                                    <rect width="100%" height="100%" fill="url(#carbonSheen)" />
                                </svg>
                            </div>
                            {/* Arrow pointing down */}
                            <div
                                className="absolute left-1/2 -translate-x-1/2"
                                style={{ bottom: -14 }}
                            >
                                <div
                                    style={{
                                        width: 0,
                                        height: 0,
                                        borderLeft: '12px solid transparent',
                                        borderRight: '12px solid transparent',
                                        borderTop: '14px solid #1565c0',
                                    }}
                                />
                            </div>
                        </div>

                        {/* RealisticPlate preview below the arrow */}
                        <div className="mt-4 flex justify-center" style={{ width: 220 }}>
                            <div style={{ width: 520 * 0.42, height: 110 * 0.42 }}>
                                <div style={{ width: 520, height: 110, transform: 'scale(0.42)', transformOrigin: 'top left' }}>
                                    <RealisticPlate
                                        plateSize={resolvedSize}
                                        plateType={resolvedType}
                                        vehicleCategory={resolvedCategory}
                                        input={{ cityCode, letters, numbers }}
                                        interactive={false}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right: Description + features */}
                    <div className="flex-1 min-w-0">
                        <p className="text-gray-600 text-sm mb-4">
                            Holen Sie sich die sportliche Carbonoptik für Ihr Kennzeichen und machen Sie es zu einem echten
                            Hingucker. Unsere Kennzeichen sind DIN geprüft, unter DIN CERTCO Zertifikat Nummer 1M/562431
                            eingetragen und somit unbeschränkt für den Straßenverkehr zugelassen.
                        </p>
                        <ul className="space-y-3">
                            <li className="flex items-center gap-3 text-sm text-gray-800">
                                <FaCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                Sportliches Design
                            </li>
                            <li className="flex items-center gap-3 text-sm text-gray-800">
                                <FaCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                100% Amtlich zugelassen
                            </li>
                            <li className="flex items-center gap-3 text-sm text-gray-800">
                                <FaCheck className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                Hochwertige Carbonoptik
                            </li>
                        </ul>
                    </div>
                </div>

                {/* Buttons */}
                <div className="flex items-center gap-6 mt-8">
                    <button
                        onClick={onAccept}
                        className="px-8 py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-lg shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] uppercase tracking-wide"
                    >
                        Ja, hinzufügen
                    </button>
                    <button
                        onClick={onDecline}
                        className="text-gray-400 hover:text-gray-600 font-medium text-sm uppercase tracking-wide transition-colors"
                    >
                        Nein, danke
                    </button>
                </div>
            </motion.div>
        </div>
    )
}
